import React, { useState, useEffect } from 'react';
import Spinner from '../components/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import './Purchases.css';
import './DetailPages.css';

function PurchaseDetail({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [purchase, setPurchase] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    if (tenantId && id) {
      loadData();
    }
  }, [tenantId, id]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTenant(),
        loadPurchase()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadPurchase = async () => {
    try {
      const data = await api.get(`/purchases/${id}`, tenantId);
      setPurchase(data);
      if (data.locationId) {
        loadLocation(data.locationId);
      }
    } catch (error) {
      console.error('Error loading purchase:', error);
      showNotification('Fehler beim Laden der Anschaffung', 'error');
      navigate('/purchases');
    }
  };

  const loadLocation = async (locationId) => {
    try {
      const data = await api.get(`/locations/${locationId}`, tenantId);
      setLocation(data);
    } catch (error) {
      console.error('Error loading location:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency === 'CHF' ? 'CHF' : 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return <Spinner />;
  }

  if (!purchase) {
    return <div>Anschaffung nicht gefunden</div>;
  }

  const items = purchase.items || [];
  const totalAmount = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

  return (
    <div className="purchase-detail">
      <div className="detail-header">
        <button type="button" onClick={() => navigate('/purchases')} className="btn-secondary">
          ← Zurück
        </button>
        <h1>Anschaffung: {purchase.supplier || 'Unbekannt'}</h1>
        <button type="button" onClick={() => navigate(`/purchases/${id}/edit`)} className="btn-primary">
          Bearbeiten
        </button>
      </div>

      <div className="detail-section">
        <h2>Allgemeine Informationen</h2>
        <p><strong>Lieferant:</strong> {purchase.supplier || '-'}</p>
        <p><strong>Datum:</strong> {formatDate(purchase.date)}</p>
        {purchase.notes && <p><strong>Notizen:</strong> {purchase.notes}</p>}
      </div>

      {location && (
        <div className="detail-section">
          <h2>Standort</h2>
          <p>
            <span 
              className="link-button"
              onClick={() => navigate(`/locations/${location.id}`)}
            >
              {location.name}
            </span>
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="detail-section">
          <h2>Artikel</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Menge</th>
                <th>Einheit</th>
                <th>Einzelpreis</th>
                <th>Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.name || '-'}</td>
                  <td>{item.quantity || 1}</td>
                  <td>{item.unit || 'Stück'}</td>
                  <td>{formatCurrency(item.price || 0)}</td>
                  <td>{formatCurrency((item.price || 0) * (item.quantity || 1))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4"><strong>Gesamtbetrag:</strong></td>
                <td><strong>{formatCurrency(totalAmount)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default PurchaseDetail;

